import React, { useCallback } from 'react';
import Iconify from '@emfood/shared/src/components/iconify';

import {
  Box,
  Chip,
  Checkbox,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
} from '@mui/material';

export interface FavouritesCollectionOption {
  id: string;
  name: string;
  isShared?: boolean;
}

interface FavouritesMultiselectProps {
  options: FavouritesCollectionOption[];
  value: string[];
  onChange: (ids: string[]) => void;
}

export default function FavouritesMultiselect({
  options,
  value,
  onChange,
}: FavouritesMultiselectProps) {
  const selectedOptions = options.filter((option) => value.includes(option.id));

  const handleDelete = useCallback(
    (idToDelete: string) => {
      onChange(value.filter((id) => id !== idToDelete));
    },
    [value, onChange]
  );

  const handleChipKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, id: string) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDelete(id);
      }
    },
    [handleDelete]
  );

  return (
    <Box width="100%">
      <Autocomplete
        multiple
        disableCloseOnSelect
        disabled={options.length === 0}
        value={selectedOptions}
        options={options}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        aria-label="Select favourites lists to filter"
        id="favouriteCollectionIds-autocomplete"
        ListboxProps={{
          id: 'favouriteCollectionIds-listbox',
          role: 'listbox',
          'aria-multiselectable': 'true',
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            const listbox = document.getElementById('favouriteCollectionIds-listbox');
            if (listbox) {
              const firstItem = listbox.querySelector('[role="option"]');
              if (firstItem) {
                (firstItem as HTMLElement)?.focus();
              }
            }
          }
        }}
        onChange={(_event, newValue) => {
          onChange(newValue.map((option) => option.id));
          const input = document.getElementById('favouriteCollectionIds-autocomplete');
          if (input) {
            input.focus();
          }
        }}
        renderOption={(props, option, { selected }) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/prop-types
          const { className, ...rest } = props;
          return (
            <Box
              component="li"
              {...rest}
              key={option.id}
              role="option"
              aria-selected={selected}
              aria-label={`${option.name}${option.isShared ? ', shared list' : ''}`}
              sx={{
                color: 'grey.700',
                p: 2,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-focused': {
                  backgroundColor: 'action.focus',
                },
              }}
            >
              <Checkbox
                checked={selected}
                sx={{
                  mr: 1,
                  color: 'grey.700',
                  '&.Mui-checked': {
                    color: 'grey.800',
                  },
                }}
                inputProps={{
                  'aria-label': selected ? `Remove ${option.name}` : `Select ${option.name}`,
                  role: 'checkbox',
                }}
              />
              <span>{option.name}</span>
              {option.isShared && (
                <Iconify
                  icon="eva:people-fill"
                  width={16}
                  aria-hidden
                  sx={{ ml: 1, color: 'text.secondary', flexShrink: 0 }}
                />
              )}
            </Box>
          );
        }}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Favourites Lists"
            placeholder="Search favourites lists"
            variant="outlined"
            fullWidth
            helperText={
              options.length === 0
                ? 'No favourites lists yet — create one from the Favourites page'
                : undefined
            }
            inputProps={{
              ...params.inputProps,
              'aria-controls': 'favouriteCollectionIds-listbox',
              'aria-expanded': 'true',
            }}
            sx={{
              '& .MuiInputBase-root': {
                color: 'grey.700',
                '&:hover': {
                  color: 'grey.800',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'grey.700',
                '&.Mui-focused': {
                  color: 'grey.800',
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.700',
                '&:hover': {
                  borderColor: 'grey.800',
                },
                '&.Mui-focused': {
                  borderColor: 'grey.800',
                },
              },
            }}
          />
        )}
      />
      {selectedOptions.length > 0 && (
        <Box
          sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}
          aria-label="Selected favourites lists"
          role="region"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              borderStyle: 'dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1,
            }}
          >
            <Typography variant="h4" sx={{ mr: 1 }} component="h3">
              Favourites:
            </Typography>
            {selectedOptions.map((option) => (
              <Chip
                key={option.id}
                label={option.name}
                icon={
                  option.isShared ? (
                    <Iconify icon="eva:people-fill" width={16} aria-hidden />
                  ) : undefined
                }
                onDelete={() => handleDelete(option.id)}
                onClick={() => handleDelete(option.id)}
                onKeyDown={(e) => handleChipKeyDown(e, option.id)}
                size="small"
                tabIndex={0}
                aria-label={`Remove ${option.name} from Favourites`}
                sx={{
                  '&:last-child': { mr: 1 },
                  '&:focus': {
                    backgroundColor: 'grey.300',
                    color: 'black',
                  },
                }}
              />
            ))}
          </Box>
          <IconButton
            onClick={() => onChange([])}
            size="small"
            aria-label="Clear favourites filter"
            sx={{
              '&:focus': {
                outline: '2px solid',
                outlineOffset: '2px',
              },
            }}
          >
            <Iconify icon="solar:trash-bin-trash-bold" />
            <Typography variant="subtitle2">Clear</Typography>
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
